<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useFavoriteWhatsAppTemplates } from 'dashboard/composables/useFavoriteWhatsAppTemplates';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import WhatsappTemplate from './WhatsappTemplate.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['sendMessage']);

const { t } = useI18n();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const inboxesUiFlags = useMapGetter('inboxes/getUIFlags');
const {
  isFavoriteTemplate,
  toggleFavoriteTemplate,
  sortTemplatesByFavorite,
  favoriteTemplatesFrom,
} = useFavoriteWhatsAppTemplates(computed(() => props.inboxId));

const searchQuery = ref('');
const selectedTemplate = ref(null);

// Gates the trigger while ComposeConversation's on-open refetch is in flight,
// so an agent can't open the picker before a stale template list is refreshed.
const isRefreshingTemplates = computed(() => inboxesUiFlags.value.isFetching);

const whatsAppTemplateMessages = computed(() => {
  return getFilteredWhatsAppTemplates.value(props.inboxId);
});

const favoriteTemplates = computed(() =>
  favoriteTemplatesFrom(whatsAppTemplateMessages.value).slice(0, 6)
);

const filteredTemplates = computed(() => {
  const filtered = whatsAppTemplateMessages.value.filter(template =>
    template.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
  return sortTemplatesByFavorite(filtered);
});

const getTemplateBody = template => {
  return template.components.find(component => component.type === 'BODY').text;
};

const handlePopoverShow = () => {
  searchQuery.value = '';
  selectedTemplate.value = null;
};

const handlePopoverHide = () => {
  selectedTemplate.value = null;
};

const handleTemplateClick = template => {
  selectedTemplate.value = template;
};

const handleToggleFavorite = (event, template) => {
  event.stopPropagation();
  toggleFavoriteTemplate(template);
};

const handleBack = () => {
  selectedTemplate.value = null;
};

const handleSendMessage = (template, hide) => {
  emit('sendMessage', template);
  hide();
};
</script>

<template>
  <Popover
    align="start"
    disable-mobile-view
    @show="handlePopoverShow"
    @hide="handlePopoverHide"
  >
    <Button
      icon="i-ri-whatsapp-line"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.LABEL')"
      color="slate"
      size="sm"
      :disabled="selectedTemplate || isRefreshingTemplates"
      class="!text-xs font-medium"
    />
    <template #content="{ hide }">
      <div
        v-if="!selectedTemplate"
        class="flex flex-col gap-2 p-4 items-center w-[21.875rem]"
      >
        <div
          v-if="favoriteTemplates.length"
          class="flex w-full flex-col gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
        >
          <p class="mb-0 text-xs font-medium uppercase tracking-wide text-n-slate-11">
            {{
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.FAVORITES.TITLE'
              )
            }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="template in favoriteTemplates"
              :key="`favorite-${template.id}`"
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-n-weak bg-n-solid-2 px-3 py-1.5 text-xs text-n-slate-12 transition hover:bg-n-alpha-1"
              @click="handleTemplateClick(template)"
            >
              <span class="i-lucide-star size-3.5 text-amber-9" />
              <span class="truncate max-w-[13rem]">{{ template.name }}</span>
            </button>
          </div>
        </div>
        <div class="relative w-full">
          <Icon
            icon="i-lucide-search"
            class="absolute size-3.5 top-2 ltr:left-3 rtl:right-3"
          />
          <input
            v-model="searchQuery"
            type="search"
            :placeholder="
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
              )
            "
            class="w-full h-8 py-2 ltr:pl-10 rtl:pr-10 ltr:pr-2 rtl:pl-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
          />
        </div>
        <div
          v-for="template in filteredTemplates"
          :key="template.id"
          class="flex w-full cursor-pointer items-start justify-between gap-2 rounded-lg p-2 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
          @click="handleTemplateClick(template)"
        >
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <div class="flex items-center gap-2">
              <span class="text-sm text-n-slate-12">{{ template.name }}</span>
              <span
                v-if="isFavoriteTemplate(template)"
                class="rounded-full bg-amber-3 px-2 py-0.5 text-[10px] font-medium text-amber-11"
              >
                {{
                  t(
                    'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.FAVORITES.BADGE'
                  )
                }}
              </span>
            </div>
            <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-2">
              {{ getTemplateBody(template) }}
            </p>
          </div>
          <button
            type="button"
            class="mt-0.5 inline-flex size-8 flex-shrink-0 items-center justify-center rounded-full text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-amber-10"
            :aria-label="
              isFavoriteTemplate(template)
                ? t(
                    'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.FAVORITES.REMOVE'
                  )
                : t(
                    'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.FAVORITES.ADD'
                  )
            "
            @click="handleToggleFavorite($event, template)"
          >
            <span
              :class="
                isFavoriteTemplate(template)
                  ? 'i-lucide-star size-4 fill-current text-amber-9'
                  : 'i-lucide-star size-4'
              "
            />
          </button>
        </div>
        <template v-if="filteredTemplates.length === 0">
          <p class="pt-2 w-full text-sm text-n-slate-11">
            {{
              t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.EMPTY_STATE')
            }}
          </p>
        </template>
      </div>
      <WhatsappTemplate
        v-else
        :template="selectedTemplate"
        @send-message="payload => handleSendMessage(payload, hide)"
        @back="handleBack"
      />
    </template>
  </Popover>
</template>
